import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../lib/AuthProvider';
import { TrendingUp, Target, DollarSign, Calendar, Play, AlertCircle, Search, Filter, X, RefreshCw, Clock, Eye, ChevronLeft, ChevronRight, Image as ImageIcon, Film } from 'lucide-react';
import toast from 'react-hot-toast';

interface PromotablePost {
  id: string;
  content: string;
  image_url?: string;
  video_url?: string;
  platform: string;
  platform_post_id?: string;
  platform_post_url?: string;
  source_table: 'ready_posts' | 'posts';
  published_at: string;
  updated_at: string;
}

interface AdPromotion {
  id: string;
  post_id: string;
  status: string;
  phase: number;
  budget_daily: number;
  target_type: string;
  metrics: { reach?: number };
}

export default function AdsBoosterPosts() {
  const { user } = useAuth();
  const [posts, setPosts] = useState<PromotablePost[]>([]);
  const [promotions, setPromotions] = useState<AdPromotion[]>([]);
  const [loading, setLoading] = useState(true);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState<{ id: string; platform: string } | null>(null);
  const [customBudget, setCustomBudget] = useState(20);
  const [customDuration, setCustomDuration] = useState(7);
  const [searchText, setSearchText] = useState('');
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [daysRange, setDaysRange] = useState(90);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  useEffect(() => {
    if (user) fetchData();
  }, [user, daysRange]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, filterPlatform]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const daysAgo = new Date();
      daysAgo.setDate(daysAgo.getDate() - daysRange);
      const cutoff = daysAgo.toISOString();

      // Fetch from BOTH tables in parallel
      const [readyPostsRes, postsRes, promoRes] = await Promise.all([
        // 1) ready_posts — published or error (error posts were still created on the platform)
        supabase.from('ready_posts')
          .select('id, content, image_url, platform, platform_post_id, platform_post_url, platform_post_created_at, publication_status, updated_at')
          .eq('user_id', user?.id)
          .in('publication_status', ['published', 'error'])
          .gte('updated_at', cutoff)
          .order('updated_at', { ascending: false })
          .limit(200),
        // 2) posts — published status
        supabase.from('posts')
          .select('id, content, image_url, video_url, platform, platform_post_id, platform_post_url, published_at, status, updated_at')
          .eq('user_id', user?.id)
          .eq('status', 'published')
          .gte('updated_at', cutoff)
          .order('updated_at', { ascending: false })
          .limit(200),
        // 3) existing promotions
        supabase.from('ad_promotions')
          .select('id, post_id, status, phase, budget_daily, target_type, metrics')
          .eq('user_id', user?.id)
      ]);

      // Normalize ready_posts
      const fromReady: PromotablePost[] = (readyPostsRes.data || []).map((p: any) => ({
        id: p.id,
        content: p.content || '',
        image_url: p.image_url,
        platform: p.platform,
        platform_post_id: p.platform_post_id,
        platform_post_url: p.platform_post_url,
        source_table: 'ready_posts' as const,
        published_at: p.platform_post_created_at || p.updated_at,
        updated_at: p.updated_at,
      }));

      // Normalize posts
      const fromPosts: PromotablePost[] = (postsRes.data || []).map((p: any) => ({
        id: p.id,
        content: p.content || '',
        image_url: p.image_url,
        video_url: p.video_url,
        platform: p.platform,
        platform_post_id: p.platform_post_id,
        platform_post_url: p.platform_post_url,
        source_table: 'posts' as const,
        published_at: p.published_at || p.updated_at,
        updated_at: p.updated_at,
      }));

      // Merge & deduplicate by content similarity (first 100 chars) + platform
      const seen = new Set<string>();
      const merged: PromotablePost[] = [];

      // Posts table has priority (it's the final publication record)
      for (const p of [...fromPosts, ...fromReady]) {
        const key = `${p.platform}::${(p.content || '').substring(0, 100)}`;
        if (!seen.has(key)) {
          seen.add(key);
          merged.push(p);
        }
      }

      // Sort by most recent
      merged.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());

      setPosts(merged);
      setPromotions(promoRes.data || []);
    } catch (error) {
      console.error(error);
      toast.error('Błąd ładowania postów');
    } finally {
      setLoading(false);
    }
  };

  const createPromotion = async () => {
    if (!selectedPost) return;
    try {
      toast.loading('Tworzenie promocji...', { id: 'promo' });
      const { data: settings } = await supabase.from('ad_booster_settings').select('phase1_target_type').eq('user_id', user?.id).maybeSingle();
      
      const { data: promo, error } = await supabase.from('ad_promotions').insert({
        post_id: selectedPost.id, user_id: user?.id, platform: selectedPost.platform, phase: 1,
        budget_daily: customBudget, duration_days: customDuration, target_type: settings?.phase1_target_type || 'page_fans',
        status: 'pending'
      }).select('id').single();

      if (error || !promo) throw error;
      
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ads-campaign-launcher`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
        body: JSON.stringify({ promotion_id: promo.id })
      });

      if (!res.ok) throw new Error((await res.json()).error || 'Failed');
      
      toast.success('Promocja uruchomiona!', { id: 'promo' });
      setShowBudgetModal(false);
      fetchData();
    } catch (e) {
      console.error(e);
      toast.error('Błąd publikacji Meta API', { id: 'promo' });
    }
  };

  const filteredPosts = posts.filter(p => {
    if (filterPlatform !== 'all' && p.platform !== filterPlatform) return false;
    if (searchText && !p.content.toLowerCase().includes(searchText.toLowerCase())) return false;
    return true;
  });

  const totalPages = Math.ceil(filteredPosts.length / itemsPerPage);
  const paginatedPosts = filteredPosts.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const platformColors: Record<string, string> = {
    facebook: 'bg-blue-600/80',
    instagram: 'bg-gradient-to-r from-purple-600 to-pink-500',
    instagram_direct: 'bg-gradient-to-r from-fuchsia-600 to-orange-500',
  };

  const platformLabels: Record<string, string> = {
    facebook: 'Facebook',
    instagram: 'Instagram',
    instagram_direct: 'IG Reels',
  };

  const uniquePlatforms = [...new Set(posts.map(p => p.platform))];

  return (
    <div className="space-y-6 animate-in fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between bg-[#0f172a] p-4 rounded-2xl border border-slate-800 gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-500/10 rounded-lg"><TrendingUp className="w-5 h-5 text-purple-400" /></div>
          <div>
            <h2 className="text-xl font-bold text-white">Promocja Postów (AI Booster)</h2>
            <p className="text-xs text-slate-500 mt-0.5">{filteredPosts.length} postów do promowania</p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <select value={filterPlatform} onChange={e => setFilterPlatform(e.target.value)}
            className="px-4 py-2.5 bg-slate-900 border border-slate-700 rounded-xl text-white text-sm font-bold focus:outline-none focus:ring-2 focus:ring-purple-500">
            <option value="all">Wszystkie ({posts.length})</option>
            {uniquePlatforms.map(p => (
              <option key={p} value={p}>{platformLabels[p] || p} ({posts.filter(pp => pp.platform === p).length})</option>
            ))}
          </select>
          <select value={daysRange} onChange={e => setDaysRange(Number(e.target.value))}
            className="px-4 py-2.5 bg-slate-900 border border-slate-700 rounded-xl text-white text-sm font-bold focus:outline-none focus:ring-2 focus:ring-purple-500">
            <option value={30}>Ostatnie 30 dni</option>
            <option value={60}>Ostatnie 60 dni</option>
            <option value={90}>Ostatnie 90 dni</option>
            <option value={180}>Ostatnie 180 dni</option>
            <option value={365}>Ostatni rok</option>
          </select>
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input type="text" placeholder="Szukaj..." value={searchText} onChange={e => setSearchText(e.target.value)}
              className="w-48 pl-10 pr-4 py-2.5 bg-slate-900 border border-slate-700 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 placeholder:text-slate-500" />
          </div>
          <button onClick={fetchData} className="p-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl border border-slate-700 transition-colors">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <div className="w-12 h-12 border-4 border-slate-700 border-t-purple-500 rounded-full animate-spin" />
          <p className="text-slate-500 text-sm font-bold">Ładowanie postów z obu tabel...</p>
        </div>
      ) : filteredPosts.length === 0 ? (
        <div className="text-center py-20 bg-[#0f172a] rounded-3xl border border-slate-800 flex flex-col items-center">
          <TrendingUp className="w-12 h-12 text-slate-500 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-white mb-2">Brak postów do promocji</h3>
          <p className="text-slate-400 max-w-md">Nie znaleziono opublikowanych postów z ostatnich {daysRange} dni. Zaplanuj i opublikuj posty w zakładce Kalendarz, aby pojawiły się tutaj.</p>
          <button onClick={() => setDaysRange(365)} className="mt-4 px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-xl text-sm font-bold transition-colors">
            Pokaż z ostatniego roku
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {paginatedPosts.map(post => {
              const promo = promotions.find(p => p.post_id === post.id);
              const hasMedia = post.image_url || post.video_url;
              const publishDate = new Date(post.published_at);
              return (
                <div key={`${post.source_table}-${post.id}`} className="bg-[#0f172a] rounded-[1.5rem] border border-slate-800 flex flex-col overflow-hidden hover:border-purple-500/30 transition-all group">
                  {/* Media */}
                  <div className="h-48 bg-slate-900/50 relative overflow-hidden">
                    {post.video_url ? (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800">
                        <Film className="w-12 h-12 text-purple-400/60" />
                        <span className="absolute bottom-3 right-3 px-2 py-1 bg-black/60 rounded text-[10px] font-bold text-purple-300 border border-purple-500/30">VIDEO</span>
                      </div>
                    ) : post.image_url ? (
                      <img src={post.image_url} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800">
                        <TrendingUp className="w-10 h-10 text-slate-700" />
                      </div>
                    )}
                    {/* Platform Badge */}
                    <div className={`absolute top-3 left-3 px-2.5 py-1 ${platformColors[post.platform] || 'bg-slate-600'} backdrop-blur text-white text-[10px] font-bold rounded-lg uppercase tracking-wider shadow-lg`}>
                      {platformLabels[post.platform] || post.platform}
                    </div>
                    {/* Date Badge */}
                    <div className="absolute top-3 right-3 px-2 py-1 bg-black/60 backdrop-blur text-slate-300 text-[10px] font-bold rounded flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {publishDate.toLocaleDateString('pl-PL', { day: 'numeric', month: 'short' })}
                    </div>
                    {/* External link */}
                    {post.platform_post_url && (
                      <a href={post.platform_post_url} target="_blank" rel="noopener noreferrer"
                        className="absolute bottom-3 left-3 px-2 py-1 bg-black/60 backdrop-blur text-blue-300 text-[10px] font-bold rounded flex items-center gap-1 hover:bg-blue-600/50 transition-colors"
                        onClick={e => e.stopPropagation()}>
                        <Eye className="w-3 h-3" /> Zobacz na platformie
                      </a>
                    )}
                  </div>
                  {/* Content */}
                  <div className="p-5 flex-1 flex flex-col">
                    <p className="text-slate-300 text-sm line-clamp-3 mb-4 leading-relaxed">{post.content}</p>
                    
                    <div className="mt-auto">
                      {promo ? (
                        <div className="bg-slate-800/50 p-3 rounded-xl border border-slate-700">
                          <div className="flex justify-between items-center mb-2">
                            <span className={`text-xs px-2.5 py-1 rounded-lg font-bold flex items-center gap-1 ${promo.status==='active' ? 'bg-emerald-500/20 text-emerald-400' : promo.status === 'pending' ? 'bg-amber-500/20 text-amber-400' : 'bg-slate-500/20 text-slate-400'}`}>
                              {promo.status === 'active' && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping" />}
                              {promo.status === 'active' ? 'Aktywna' : promo.status === 'pending' ? 'Oczekuje' : promo.status}
                            </span>
                            <span className="text-xs text-slate-400 font-bold">{promo.budget_daily} PLN/dz</span>
                          </div>
                          <div className="text-xs text-slate-400 flex justify-between">
                            <span>Cel: {promo.target_type}</span>
                            {promo.metrics?.reach && <span className="text-purple-400 font-bold">{promo.metrics.reach} zasięgu</span>}
                          </div>
                        </div>
                      ) : (
                        <button onClick={() => { setSelectedPost({ id: post.id, platform: post.platform }); setShowBudgetModal(true); }}
                          className="w-full py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white rounded-xl font-bold flex items-center justify-center gap-2 transition-all shadow-lg shadow-purple-500/20 hover:shadow-purple-500/40 hover:scale-[1.02] active:scale-[0.98]">
                          <Play className="w-4 h-4 fill-current" /> Promuj Post
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 md:gap-4 mt-8 pt-6 border-t border-slate-800">
              <button 
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="flex items-center gap-1 md:gap-2 px-3 py-2 md:px-4 md:py-2.5 rounded-xl bg-slate-800 text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-700 transition-colors font-bold text-sm">
                <ChevronLeft className="w-4 h-4" /> Poprzednia
              </button>
              <div className="flex gap-1">
                {Array.from({ length: totalPages }).map((_, i) => {
                  const pageNum = i + 1;
                  if (totalPages > 5 && pageNum !== 1 && pageNum !== totalPages && Math.abs(pageNum - currentPage) > 1) {
                    if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                      return <span key={i} className="text-slate-500 px-2 py-2">...</span>;
                    }
                    return null;
                  }
                  return (
                    <button key={i} onClick={() => setCurrentPage(pageNum)}
                      className={`w-9 h-9 rounded-xl font-bold text-sm transition-colors ${currentPage === pageNum ? 'bg-purple-600 text-white shadow-[0_0_10px_rgba(147,51,234,0.5)]' : 'bg-[#0f172a] text-slate-400 hover:bg-slate-800 border border-slate-800'}`}>
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button 
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="flex items-center gap-1 md:gap-2 px-3 py-2 md:px-4 md:py-2.5 rounded-xl bg-slate-800 text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-700 transition-colors font-bold text-sm">
                Następna <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </>
      )}

      {/* Budget Modal */}
      {showBudgetModal && selectedPost && (
        <div className="fixed inset-0 bg-[#020617]/80 backdrop-blur-xl z-50 flex items-center justify-center p-4">
          <div className="bg-[#0f172a] rounded-[2rem] w-full max-w-md border border-slate-700/60 shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-6 flex justify-between items-start">
              <h3 className="text-2xl font-black text-white">Promuj Post</h3>
              <button onClick={() => setShowBudgetModal(false)} className="w-8 h-8 bg-black/20 hover:bg-black/40 rounded-full flex items-center justify-center text-white"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-6">
              <div>
                <label className="flex justify-between mb-2"><span className="text-sm font-bold text-slate-300">Budżet Dzienny</span><span className="text-lg font-black text-white">{customBudget} PLN</span></label>
                <input type="range" min={5} max={500} step={5} value={customBudget} onChange={e => setCustomBudget(Number(e.target.value))} className="w-full h-2 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-purple-500" />
              </div>
              <div>
                <label className="flex justify-between mb-2"><span className="text-sm font-bold text-slate-300">Dni Emisji</span><span className="text-lg font-black text-white">{customDuration}</span></label>
                <input type="range" min={1} max={30} value={customDuration} onChange={e => setCustomDuration(Number(e.target.value))} className="w-full h-2 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-indigo-500" />
              </div>
              <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-400">Szacunkowy koszt:</span>
                  <span className="text-xl font-black text-white">{customBudget * customDuration} PLN</span>
                </div>
              </div>
            </div>
            <div className="p-4 bg-slate-900 border-t border-slate-800 flex gap-3">
              <button onClick={() => setShowBudgetModal(false)} className="px-5 py-3 rounded-xl font-bold text-slate-300 hover:bg-slate-800">Anuluj</button>
              <button onClick={createPromotion} className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 hover:from-purple-500 hover:to-indigo-500 transition-all"><Play className="w-4 h-4 fill-current"/> Uruchom</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
